import { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authApi } from '@/api/endpoints/auth';
import type { LoginRequest, Manager, Permission } from '@/api/endpoints/auth';
import { connectSocket, disconnectSocket } from '@/api/socket';
import { encerrarPushNativo, iniciarPushNativo } from '@/lib/pushNativo';

interface AuthContextType {
  manager: Manager | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType>({
  manager: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  can: () => false,
});

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [manager, setManager] = useState<Manager | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('manager_token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const { manager: managerData } = await authApi.getMe();
      setManager(managerData);
      connectSocket(token);
      // App nativo: push da Central para quem atende (no navegador é no-op).
      if (managerData.permissions?.includes('support')) void iniciarPushNativo();
    } catch {
      localStorage.removeItem('manager_token');
      localStorage.removeItem('manager_info');
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginRequest) => {
    const response = await authApi.login(credentials);
    localStorage.setItem('manager_token', response.token);
    localStorage.setItem('manager_info', JSON.stringify(response.manager));
    setManager(response.manager);
    connectSocket(response.token);
    // Senha temporária → força a troca antes de entrar.
    window.location.href = response.manager.must_change_password ? '/trocar-senha' : '/';
  };

  const can = (permission: Permission): boolean => !!manager?.permissions?.includes(permission);

  const logout = async () => {
    try {
      await encerrarPushNativo();
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      disconnectSocket();
      localStorage.removeItem('manager_token');
      localStorage.removeItem('manager_info');
      setManager(null);
      window.location.href = '/login';
    }
  };

  return (
    <AuthContext.Provider
      value={{
        manager,
        isAuthenticated: !!manager,
        isLoading,
        login,
        logout,
        can,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
